import { Meta, Spinner, theme } from 'aimo-ui';
import { PermitElementRow } from 'components/services/PermitElementRow';
import React from 'react';
import { useTranslation } from 'react-i18next';
import PermitParkingSpace from 'screens/permit/PermitParkingSpace';
import { PermitContentWrapper } from 'screens/permit/styles';
import { usePermitStore } from 'stores/permitStore';
import { ColumnWithMargins, RowSpaceBetween } from 'styles/ContainerStyles';
import { Maybe, ParkingSpace, Validity } from 'types/generatedSchemaTypes';
import { formatPermitValid } from 'utils/permitUtils';
import { useUiStore } from 'stores/uiStore';

const AccessPermitAvailability = ({
  availability = 0,
  loading,
  validity,
  isPooling = false,
  capacityHidden,
  parkingSpaces = [],
  permitId,
  spotMap,
  permitType,
  freetextParkingSpaceNo,
}: {
  availability: number;
  loading: boolean;
  validity?: Maybe<Validity>;
  isPooling: boolean;
  capacityHidden: boolean;
  parkingSpaces?: Maybe<ParkingSpace[]>;
  permitId?: string;
  spotMap?: Maybe<string>;
  permitType?: string;
  freetextParkingSpaceNo: Maybe<string> | undefined;
}) => {
  const { t } = useTranslation();
  const setSelectedPermitId = usePermitStore(
    (state) => state.setSelectedPermitId
  );

  const { validFrom, validTo } = validity || {};
  const hasParkingSpaces =
    (parkingSpaces ?? []).length > 0 || Boolean(freetextParkingSpaceNo);

  return (
    <PermitContentWrapper>
      {isPooling && !capacityHidden && (
        <RowSpaceBetween
          padding={15}
          withBottomBorder={!!validity || hasParkingSpaces}
          borderColor={
            validity || hasParkingSpaces ? 'rgba(0,0,0, .04)' : undefined
          }>
          <ColumnWithMargins>
            <Meta>{t('permit.access.availability')}</Meta>
          </ColumnWithMargins>
          <ColumnWithMargins>
            {loading ? (
              <Spinner small={true} />
            ) : (
              <Meta>
                {t('permit.access.spot', {
                  count: availability,
                })}
              </Meta>
            )}
          </ColumnWithMargins>
        </RowSpaceBetween>
      )}
      {validFrom && (
        <PermitElementRow
          label={t('permit.access.validFrom')}
          detail={formatPermitValid(validFrom, permitType === 'DIGITAL_PERMIT')}
          withBottomBorder={!!validTo}
          labelColor={theme.colors.black}
        />
      )}
      {validTo && (
        <PermitElementRow
          label={t('permit.access.validTo')}
          detail={formatPermitValid(validTo, permitType === 'DIGITAL_PERMIT')}
          withBottomBorder={hasParkingSpaces}
          labelColor={theme.colors.black}
        />
      )}
      {hasParkingSpaces && (
        <PermitElementRow
          label={t('longTermParking.parkingSpace')}
          labelColor={theme.colors.black}
          detailColor={theme.colors.black}
          detail=""
          icon={
            <PermitParkingSpace
              parkingSpaces={parkingSpaces ?? []}
              spotMap={spotMap}
              meta={true}
              freetextParkingSpaceNo={freetextParkingSpaceNo}
              textColor={theme.colors.black}
            />
          }
          onPress={() => {
            if (parkingSpaces) {
              setSelectedPermitId(permitId);
              useUiStore.getState().setSheetModal('parkingSpacesByPermitId');
            }
          }}
        />
      )}
    </PermitContentWrapper>
  );
};

export default AccessPermitAvailability;
